import TransactionLogger from '../actions/transactionLogger';
import {
  COVERAGE_ATTRIBUTE_ESCALATED_EXPIRED_STATUS_ID,
  COVERAGE_ATTRIBUTE_ESCALATED_NON_SUBMITTED_STATUS_ID,
  COVERAGE_ATTRIBUTE_ESCALATED_REJECTED_STATUS_ID,
  COVERAGE_ATTRIBUTE_ESCALATED_STATUS_ID,
  COVERAGE_ATTRIBUTE_REJECTED_EXPIRED_STATUS_ID,
  COVERAGE_ATTRIBUTE_REJECTED_STATUS_ID,
  LOG_TYPE_STATUS_CHANGED,
  ENTITY_TYPE_CERTIFICATE,
  LOG_TYPE_EDIT_DATA_ENTRY,
  LOG_TYPE_REFRESHED,
  STATUS_ESCALATED,
  STATUS_ON_HOLD,
  STATUS_REJECTED,
  STATUS_REMOVE_HOLD,
  ENTITY_TYPE_PROJECT_INSURED,
} from '../../../../lib/appConstants';

const CERTIFICATE_REVIEW_ACTIONS = {
  REFRESH: LOG_TYPE_REFRESHED,
  STATUS_CHANGED: LOG_TYPE_STATUS_CHANGED,
  ON_HOLD: STATUS_ON_HOLD,
  REMOVE_HOLD: STATUS_REMOVE_HOLD,
  REJECT: STATUS_REJECTED,
  ESCALATE: STATUS_ESCALATED,
  EDIT_DATA_ENTRY: LOG_TYPE_EDIT_DATA_ENTRY,
};

const normalizePerformedByDisplay = (loginProfile) => {
  if (!loginProfile) {
    return 'System';
  }

  const firstName = loginProfile.FirstName || '';
  const lastName = loginProfile.LastName || '';
  const displayName = `${firstName} ${lastName}`.trim();

  return displayName || loginProfile.Email || 'System';
};

const hasExpired = (deficiency) => {
  if (!deficiency) {
    return false;
  }

  if (String(deficiency.AttributeStatus || '').toLowerCase() === 'expired') {
    return true;
  }

  if (!deficiency.ExpirationDate) {
    return false;
  }

  return new Date(deficiency.ExpirationDate).getTime() <= new Date().getTime();
};

const getRejectedStatus = (deficiency) => {
  if (hasExpired(deficiency)) {
    return {
      id: COVERAGE_ATTRIBUTE_REJECTED_EXPIRED_STATUS_ID,
      label: 'Rejected Expired',
    };
  }

  return {
    id: COVERAGE_ATTRIBUTE_REJECTED_STATUS_ID,
    label: 'Rejected',
  };
};

const getEscalatedStatus = (deficiency) => {
  const currentStatus = String(
    deficiency.AttributeStatus || deficiency.CoverageAttributeStatus || ''
  ).toLowerCase();

  if (hasExpired(deficiency)) {
    return {
      id: COVERAGE_ATTRIBUTE_ESCALATED_EXPIRED_STATUS_ID,
      label: 'Escalated Expired',
    };
  }

  if (currentStatus === 'non submitted' || currentStatus === 'non-submitted') {
    return {
      id: COVERAGE_ATTRIBUTE_ESCALATED_NON_SUBMITTED_STATUS_ID,
      label: 'Escalated Non Submitted',
    };
  }

  if (currentStatus.indexOf('rejected') !== -1) {
    return {
      id: COVERAGE_ATTRIBUTE_ESCALATED_REJECTED_STATUS_ID,
      label: 'Escalated Rejected',
    };
  }

  return {
    id: COVERAGE_ATTRIBUTE_ESCALATED_STATUS_ID,
    label: 'Escalated',
  };
};

const mapCoverageDetails = (coverageDetails = [], action) => {
  if (!Array.isArray(coverageDetails)) {
    return [];
  }

  return coverageDetails.map((detail) => {
    const nextStatus =
      action === CERTIFICATE_REVIEW_ACTIONS.REJECT
        ? getRejectedStatus(detail)
        : getEscalatedStatus(detail);

    return {
      coverageType: detail.CoverageType || null,
      attribute: detail.AttributeName || null,
      deficiencyText: detail.DeficiencyText || null,
      currentAttributeStatus: detail.AttributeStatus || detail.CoverageAttributeStatus || null,
      currentAttributeStatusId: detail.CoverageAttributeStatusID || null,
      newAttributeStatus: nextStatus.label,
      newAttributeStatusId: nextStatus.id,
      projectInsuredDeficiencyId: detail.ProjectInsuredDeficiencyID || null,
      ruleGroupId: detail.RuleGroupID || detail.ruleGroupId || null,
      ruleId: detail.RuleID || detail.ruleId || null,
    };
  });
};

export const mapCertificateReviewPayload = async (payload, context) => {
  try {
    const {
      loginProfile,
      holderId,
      action,
      status = null,
    } = context;

    const transactionTypeCode = action;
    if (!transactionTypeCode) {
      throw new Error(`Unknown certificate review action: ${action}`);
    }

    const resolvedProjectInsuredId = payload.ProjectInsuredID || null;
    const resolvedEntityId = () => {
      if (
        status == CERTIFICATE_REVIEW_ACTIONS.ON_HOLD ||
        status == CERTIFICATE_REVIEW_ACTIONS.REMOVE_HOLD ||
        status == CERTIFICATE_REVIEW_ACTIONS.REJECT ||
        status == CERTIFICATE_REVIEW_ACTIONS.ESCALATE
      ) {
        return String(payload.CertificateID);
      } else {
        return String(resolvedProjectInsuredId);
      }
    }

    const performedByUserId = loginProfile && loginProfile.Id ? loginProfile.Id : null;
    return {
      performedByUserId,
      performedByDisplay: normalizePerformedByDisplay(loginProfile),
      holderId,
      projectId: payload.ProjectID,
      insuredId: payload.InsuredID,
      projectInsuredId: resolvedProjectInsuredId,
      entityId: payload.CertificateID || resolvedProjectInsuredId,
      transactionTypeCode,
      entityTypeCode: payload.CertificateID ? ENTITY_TYPE_CERTIFICATE : ENTITY_TYPE_PROJECT_INSURED,
      documentId: payload.DocumentId || null,
      certificateId: payload.CertificateID || null,
      idempotencyKey: TransactionLogger.generateIdempotencyKey(),
      status: status,
      metadata: {
        action: status ? status : action,
        actionSource: 'certificate_review_screen',
        timestamp: new Date().toISOString(),
        ...(Array.isArray(payload.coverageDetails) &&
          payload.coverageDetails.length > 0 && {
            coverageDetails: mapCoverageDetails(payload.coverageDetails, payload.action),
          }),
        // ...(payload.metadata || {}),
      },
    };
  } catch (error) {
    console.error('Error mapping certificate review payload:', error);
    throw error;
  }
};

export const processCertificateReviewTransaction = async (payload, context) => {
  try {
    const transactionData = await mapCertificateReviewPayload(payload, context);
    return await TransactionLogger.logTransaction(transactionData);
  } catch (error) {
    console.error('Failed to process certificate review transaction:', error);
    return false;
  }
};

export {
  CERTIFICATE_REVIEW_ACTIONS,
};

export default {
  mapCertificateReviewPayload,
  processCertificateReviewTransaction,
  CERTIFICATE_REVIEW_ACTIONS,
};
